import React, { useEffect, useState, useCallback } from 'react'
import {
  fetchUserCharacters,
  fetchUserProfile,
  searchSoloDuoCharacters,
  sendSoloDuoInvitation,
} from '../../api/api'

function SendDuoInvitationModal ({ token, isOpen, onClose, onSent }) {
  const [sendMyCharacterId, setSendMyCharacterId] = useState('')
  const [sendingToId, setSendingToId] = useState(null)
  const [sendError, setSendError] = useState('')
  const [myCharacters, setMyCharacters] = useState([])
  const [myUserId, setMyUserId] = useState(null)
  const [isLoadingModal, setIsLoadingModal] = useState(false)
  const [characterSearchQuery, setCharacterSearchQuery] = useState('')
  const [characterSearchResults, setCharacterSearchResults] = useState([])
  const [isSearchingCharacters, setIsSearchingCharacters] = useState(false)

  const loadModalData = useCallback(async () => {
    if (!token) return
    setSendError('')
    setSendMyCharacterId('')
    setCharacterSearchQuery('')
    setCharacterSearchResults([])
    setIsLoadingModal(true)
    try {
      const [charsData, profileData] = await Promise.all([
        fetchUserCharacters(token),
        fetchUserProfile(token).catch(() => null),
      ])
      const myId = profileData?.id || profileData?.userId || null
      setMyUserId(myId)
      const chars = Array.isArray(charsData) ? charsData : charsData?.characters || charsData?.data || []
      setMyCharacters(chars)
      if (chars.length > 0) {
        setSendMyCharacterId(String(chars[0].characterId || chars[0].id || ''))
      }
    } catch (err) {
      console.error('[SendDuoInvitationModal] Failed to load modal data:', err)
      setSendError('Failed to load data.')
    } finally {
      setIsLoadingModal(false)
    }
  }, [token])

  useEffect(() => {
    if (isOpen && token) {
      loadModalData()
    }
  }, [isOpen, token, loadModalData])

  useEffect(() => {
    const q = characterSearchQuery.trim()
    if (q.length < 2) {
      setCharacterSearchResults([])
      return
    }
    const t = setTimeout(async () => {
      if (!token) return
      setIsSearchingCharacters(true)
      setCharacterSearchResults([])
      try {
        const data = await searchSoloDuoCharacters(token, q, 20)
        const list = Array.isArray(data) ? data : data?.characters || data?.data || []
        setCharacterSearchResults(list)
      } catch (err) {
        console.error('[SendDuoInvitationModal] Character search failed:', err)
        setCharacterSearchResults([])
      } finally {
        setIsSearchingCharacters(false)
      }
    }, 300)
    return () => clearTimeout(t)
  }, [token, characterSearchQuery])

  const handleSendToPlayer = useCallback(async (targetUserId, label) => {
    if (!sendMyCharacterId) {
      setSendError('Please select your character first.')
      return
    }
    setSendingToId(targetUserId)
    setSendError('')
    try {
      await sendSoloDuoInvitation(token, {
        toUserId: targetUserId,
        myCharacterId: sendMyCharacterId,
      })
      onClose()
      onSent?.(label)
    } catch (err) {
      console.error('[SendDuoInvitationModal] Failed to send invitation:', err)
      setSendError(err.message || 'Failed to send invitation.')
    } finally {
      setSendingToId(null)
    }
  }, [token, sendMyCharacterId, onClose, onSent])

  const handleClose = useCallback(() => {
    setSendError('')
    onClose()
  }, [onClose])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/80 z-50 p-4">
      <div className="bg-[#1a1a2e] border border-white/10 rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh]">
        <div className="px-6 pt-6 pb-4 border-b border-white/10 flex-shrink-0">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-xl font-bold text-white">Send Duo Invitation</h2>
            <button
              type="button"
              onClick={handleClose}
              className="text-gray-400 hover:text-white transition-colors p-1"
            >
              <i className="fas fa-times text-lg"></i>
            </button>
          </div>
          <p className="text-sm text-gray-400">Select your character, then click Invite next to any player</p>
        </div>

        <div className="px-6 py-4 border-b border-white/10 flex-shrink-0">
          <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
            Your character <span className="text-red-400">*</span>
          </label>
          {isLoadingModal ? (
            <div className="flex items-center gap-2 text-gray-400 text-sm py-1">
              <div className="w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full animate-spin"></div>
              Loading...
            </div>
          ) : myCharacters.length > 0 ? (
            <select
              value={sendMyCharacterId}
              onChange={(e) => { setSendMyCharacterId(e.target.value); setSendError('') }}
              className="w-full bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              {myCharacters.map((c) => {
                const id = String(c.characterId || c.id || '')
                const realmName = c.realm?.name || c.realm || ''
                const label = [c.name, realmName].filter(Boolean).join(' — ') || id
                return <option key={id} value={id} className="bg-gray-900">{label}</option>
              })}
            </select>
          ) : (
            <input
              type="text"
              value={sendMyCharacterId}
              onChange={(e) => setSendMyCharacterId(e.target.value)}
              placeholder="Enter your character ID"
              className="w-full bg-white/10 border border-white/20 text-white placeholder-gray-500 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          )}
        </div>

        {sendError && (
          <div className="px-6 pt-3 flex-shrink-0">
            <p className="text-sm text-red-400 bg-red-900/30 border border-red-500/30 rounded-lg px-3 py-2">
              {sendError}
            </p>
          </div>
        )}

        <div className="px-6 py-4 border-b border-white/10 flex-shrink-0">
          <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
            Search by character name or BattleTag
          </label>
          <div className="relative">
            <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm"></i>
            <input
              type="text"
              value={characterSearchQuery}
              onChange={(e) => setCharacterSearchQuery(e.target.value)}
              placeholder="Type at least 2 characters (e.g. dr, PlayerName)..."
              className="w-full bg-white/10 border border-white/20 text-white placeholder-gray-500 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              autoComplete="off"
            />
            {isSearchingCharacters && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <div className="w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
          </div>
          {characterSearchQuery.trim().length > 0 && characterSearchQuery.trim().length < 2 && (
            <p className="text-xs text-gray-500 mt-1.5">Type at least 2 characters to search</p>
          )}
          {characterSearchResults.length > 0 && (
            <div className="flex flex-col gap-2 mt-3 max-h-48 overflow-y-auto">
              {characterSearchResults.map((char) => {
                const userId = char.userId || char.id
                const charName = char.name || char.characterName || 'Unknown'
                const realm = char.realm?.name || char.realm || ''
                const battletag = char.battletag || char.battleTag || ''
                const label = battletag ? `${charName} (${battletag})` : charName
                const isSendingThis = sendingToId === userId
                return (
                  <div
                    key={char.characterId || char.raiderIoId || userId || charName}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <span className="text-sm font-semibold text-white truncate block">{charName}</span>
                      <span className="text-xs text-gray-400">
                        {[realm, battletag].filter(Boolean).join(' · ')}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleSendToPlayer(userId, label)}
                      disabled={isSendingThis || !sendMyCharacterId}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-purple-600 hover:bg-purple-500 text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
                    >
                      {isSendingThis
                        ? <i className="fas fa-spinner fa-spin"></i>
                        : <i className="fas fa-paper-plane"></i>
                      }
                      {isSendingThis ? 'Sending...' : 'Invite'}
                    </button>
                  </div>
                )
              })}
            </div>
          )}
          {!isSearchingCharacters && characterSearchQuery.trim().length >= 2 && characterSearchResults.length === 0 && (
            <p className="text-xs text-gray-500 mt-2">No characters found. Try another name or BattleTag.</p>
          )}
        </div>
      </div>
    </div>
  )
}

export default SendDuoInvitationModal
